import {
    ChangeDetectorRef,
    Component,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    SimpleChanges,
} from '@angular/core';
import { ScreenSizeType } from '@models/responsive';
import { DeviceFlagsService } from '@service/device-flags.service';
import { ResponsiveService } from '@service/responsive.service';
import { AlarmLevel } from 'ops/common/models/alarm-level';
import { Channel } from 'ops/common/models/channel';
import { ChannelGroup } from 'ops/common/models/channel-group';
import { ChannelCustomGroup, CustomGroup } from 'ops/common/models/custom-group';
import { Subscription } from 'rxjs';

@Component({
    selector: 'app-ops-groups',
    templateUrl: 'ops-groups.component.html',
    styleUrls: ['./ops-groups.component.scss'],
    standalone: false,
})
export class OpsGroupsComponent implements OnInit, OnDestroy, OnChanges {
    @Input() groups: ChannelGroup[] = [];
    @Input() channels: Channel[];
    @Input() customGroups: CustomGroup[] = [];
    @Input() customChannels: ChannelCustomGroup[];
    showGroups: ChannelGroup[] = [];
    showCustomGroups: CustomGroup[] = [];

    selectedGroupId: string;

    showSearchDivider: boolean = true;

    searchName: string = '';
    searchChannelNumber: string = '';

    alarmStatusCountSubject: Subscription;

    filterOption = {
        channel: '',
        channelName: '',
        channelNo: '',
    };

    showSimpleFilter = new Set([
        ScreenSizeType.Small,
        ScreenSizeType.Medium,
        ScreenSizeType.XSmall,
    ]);

    simpleFilterLabel = {
        placeholder: 'Search Channel Name or Number',
        model: 'channel',
    };

    filterLabels = [
        {
            header: 'Ch. Name',
            placeholder: 'Search Channel Name',
            model: 'channelName',
        },
        {
            header: 'Ch. No.',
            placeholder: 'Search Channel Number',
            model: 'channelNo',
        },
    ];

    constructor(
        public responsive: ResponsiveService,
        private deviceFlagsService: DeviceFlagsService,
        private cdr: ChangeDetectorRef,
    ) {}
    ngOnChanges(changes: SimpleChanges): void {
        if (changes.groups || changes.customGroups) {
            this.applyFilters();
        }

        this.showGroups.forEach((group: any) => {
            let alarmStatusCount = this.deviceFlagsService.getLevelsByGroupId(group.id);
            group.alarms = alarmStatusCount[AlarmLevel.Alarm];
            group.cautions = alarmStatusCount[AlarmLevel.Caution];
            group.warnings = alarmStatusCount[AlarmLevel.Warning];
            this.cdr.markForCheck();
        });
    }

    ngOnInit() {
        this.applyFilters();
        this.responsive.breakPoinEmitter.subscribe((breakPoint) => {
            if (
                this.showSimpleFilter.has(breakPoint) &&
                (this.filterOption.channelName || this.filterOption.channelNo)
            ) {
                this.filterOption.channelName = '';
                this.filterOption.channelNo = '';
                this.applyFilters();
            }

            if (!this.showSimpleFilter.has(breakPoint) && this.filterOption.channel) {
                this.filterOption.channel = '';
                this.applyFilters();
            }
        });
    }

    ngAfterViewInit() {
        this.applyFilters();

        this.alarmStatusCountSubject = this.deviceFlagsService
            .onAlarmStatusCountUpdated()
            .subscribe(() => {
                this.showGroups.forEach((group: any) => {
                    let alarmStatusCount = this.deviceFlagsService.getLevelsByGroupId(group.id);
                    group.alarms = alarmStatusCount[AlarmLevel.Alarm];
                    group.cautions = alarmStatusCount[AlarmLevel.Caution];
                    group.warnings = alarmStatusCount[AlarmLevel.Warning];
                    this.cdr.markForCheck();
                });
            });
    }

    ngOnDestroy(): void {
        this.alarmStatusCountSubject?.unsubscribe();
        this.resetFilter();
    }

    applyFilters() {
        if (this.groups.length > 0) {
            this.showGroups = this.groups.map((group: any) => {
                group.alarms = 0;
                group.cautions = 0;
                group.warnings = 0;
                return group;
            });
        }
        this.showCustomGroups = this.customGroups;
    }

    filteredGroup() {
        let newShowGroup = this.groups;
        let channels = this.channels;

        channels = channels.filter((channel: Channel) =>
            this.filterOption.channelName !== ''
                ? channel.opsData.name
                      .toUpperCase()
                      .includes(this.filterOption.channelName.toUpperCase())
                : channel,
        );

        channels = channels.filter((channel: Channel) =>
            this.filterOption.channelNo !== ''
                ? channel.opsData.tag.includes(this.filterOption.channelNo)
                : channel,
        );

        channels = channels.filter((channel: Channel) =>
            this.filterOption.channel !== ''
                ? channel.opsData.name
                      .toUpperCase()
                      .includes(this.filterOption.channel.toUpperCase()) ||
                  channel.opsData.tag.includes(this.filterOption.channel)
                : channel,
        );

        let newGroup = newShowGroup.filter((group) =>
            channels.some((channel) => channel.groupId === group.id),
        );

        this.showGroups = newGroup;
    }

    onFilterChange(event) {
        this.filterOption = { ...this.filterOption, ...event };
        this.filteredGroup();
    }

    resetFilter() {
        Object.keys(this.filterOption).forEach((key) => {
            this.filterOption[key] = '';
        });
        this.showGroups = this.groups;
    }
}
